import type { OrdenCargaCombustibleSolicitarRequest } from '../types/ordenesCargaCombustible';

/**
 * Borradores locales de las dos acciones que ejecuta el operador en terreno
 * (solicitar carga, cargar/subir guía de despacho). Mismo patrón que
 * reporteDraftStorage.ts: protege solo la ventana entre "toqué Enviar" y "el
 * servidor confirmó" — no es una cola de sincronización en segundo plano.
 * Una sola ranura por tipo de acción (si se guarda un borrador nuevo, pisa al
 * anterior), igual de simple que el de reportes.
 */

const SOLICITUD_DRAFT_KEY = 'mava_orden_solicitud_borrador';
const CARGA_DRAFT_KEY = 'mava_orden_carga_borrador';

export interface OrdenSolicitudDraft {
  datos: OrdenCargaCombustibleSolicitarRequest;
  guardadoEn: string; // ISO timestamp
}

export interface OrdenCargaDraft {
  ordenId: number;
  destinoId: number;
  cantidadCargada: number;
  rutCargador: string;
  nombreCargador: string;
  horometroDeclarado?: number;
  nivelDeclarado?: number;
  fotoDataUrl: string;
  fotoNombre: string;
  fotoTipo: string;
  // Foto opcional de horómetro+nivel (ver CargarOrdenMovilView) — puede no
  // existir, a diferencia de la foto de guía de despacho de arriba.
  fotoNivelDataUrl?: string;
  fotoNivelNombre?: string;
  fotoNivelTipo?: string;
  guardadoEn: string; // ISO timestamp
}

export function guardarBorradorSolicitud(datos: OrdenCargaCombustibleSolicitarRequest): void {
  try {
    const draft: OrdenSolicitudDraft = { datos, guardadoEn: new Date().toISOString() };
    localStorage.setItem(SOLICITUD_DRAFT_KEY, JSON.stringify(draft));
  } catch (error) {
    console.warn('No se pudo guardar el borrador de la solicitud de carga:', error);
  }
}

export function obtenerBorradorSolicitud(): OrdenSolicitudDraft | null {
  try {
    const raw = localStorage.getItem(SOLICITUD_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function limpiarBorradorSolicitud(): void {
  localStorage.removeItem(SOLICITUD_DRAFT_KEY);
}

function leerComoDataUrl(archivo: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(archivo);
  });
}

export async function guardarBorradorCarga(
  ordenId: number,
  destinoId: number,
  cantidadCargada: number,
  rutCargador: string,
  nombreCargador: string,
  foto: File,
  horometroDeclarado?: number,
  nivelDeclarado?: number,
  fotoNivel?: File
): Promise<void> {
  try {
    const fotoDataUrl = await leerComoDataUrl(foto);
    const draft: OrdenCargaDraft = {
      ordenId,
      destinoId,
      cantidadCargada,
      rutCargador,
      nombreCargador,
      horometroDeclarado,
      nivelDeclarado,
      fotoDataUrl,
      fotoNombre: foto.name,
      fotoTipo: foto.type,
      guardadoEn: new Date().toISOString(),
    };
    if (fotoNivel) {
      draft.fotoNivelDataUrl = await leerComoDataUrl(fotoNivel);
      draft.fotoNivelNombre = fotoNivel.name;
      draft.fotoNivelTipo = fotoNivel.type;
    }
    localStorage.setItem(CARGA_DRAFT_KEY, JSON.stringify(draft));
  } catch (error) {
    // Foto grande + localStorage lleno (QuotaExceededError) u otro problema:
    // no bloquear el intento de envío en curso por esto, solo perdemos la
    // protección de borrador si la app se cierra antes de la respuesta.
    console.warn('No se pudo guardar el borrador de la carga:', error);
  }
}

export function obtenerBorradorCarga(): OrdenCargaDraft | null {
  try {
    const raw = localStorage.getItem(CARGA_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function limpiarBorradorCarga(): void {
  localStorage.removeItem(CARGA_DRAFT_KEY);
}

export async function dataUrlAFile(dataUrl: string, nombre: string, tipo: string): Promise<File> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], nombre, { type: tipo });
}
